import React, { useEffect, useState } from "react";
import { BaseWizardProps } from "../wizard/wizard-step-base";
import { AppDispatch, RootState } from "../../state/store";
import { useDispatch, useSelector } from "react-redux";
import {
  defaultValidate,
  WizardButtonGroup,
} from "../wizard/wizard-button-group-component";
import {
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from "@abgov/react-components";
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from "../../state/slices/notificationSlice";
import { NotificationType } from "../notifications/notification-component";
import { updateCollision } from "../../state/slices/collisionSlice";
import { Collision } from "../../models/collision";
import { isHitAndRun } from "../../state/slices/configurationSlice";

interface IntersectionsCrossingsComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected
) => TSelected = useSelector;

export const IntersectionsCrossingsComponent = ({
  navigationConfiguration,
}: IntersectionsCrossingsComponentProps) => {
  const dispatch = useAppDispatch();

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration
  );

  const defaultSelection = "0";
  const errorText = "Choose an intersection or railroad crossing scenario";
  const bannerErrorText = "Make a selection below";
  const [selection, setSelection] = useState(
    stateCollision.collision.collisionLocation
      ? stateCollision.collision.collisionLocation!.toString()
      : defaultSelection
  );
  const [nextPage, setNextPage] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let pageConfig;

    if (
      isHitAndRun(stateCollision.collision.collisionType!, stateConfiguration)
    ) {
      if (stateCollision.collision.parkingLot) {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.hitandrunyes !== undefined
        )!.hitandrunyes;
      } else {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.hitandrunno !== undefined
        )!.hitandrunno;
      }
    } else {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.default !== undefined
      )!.default;
    }

    setNextPage(pageConfig);
  }, []);

  const validate = (): boolean => {
    let valid = true;

    if (!selection || selection == defaultSelection) {
      setFormError(errorText);
      dispatch(
        pushNotification({
          body: bannerErrorText,
          notificationType: NotificationType.importantValidationError,
        })
      );
      valid = false;
    }
    return valid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        getIsCompleted(updatedCollision),
        getIsInProgress(updatedCollision)
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }
    updatedCollision.collisionLocation = parseInt(selection);

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const getIsCompleted = (updatedCollision: Collision): boolean => {
    if (navigationConfiguration.stageCompleted) {
      if (
        isHitAndRun(updatedCollision.collisionType!, stateConfiguration) &&
        updatedCollision.parkingLot
      ) {
        return true;
      }
    }

    return false;
  };

  const getIsInProgress = (updatedCollision: Collision): boolean => {
    if (navigationConfiguration.stageCompleted) {
      if (
        isHitAndRun(updatedCollision.collisionType!, stateConfiguration) &&
        updatedCollision.parkingLot
      ) {
        return false;
      }
    }
    return true;
  };

  return (
    <div>
      <h2>Intersections and railroad crossings</h2>
      <GoASpacer vSpacing={"m"} />
      Indicate the intersection or railroad crossing information that best
      matches the location of your collision. If neither applies, select 'Not
      applicable.' If you're unsure, select 'Unknown' or 'Not applicable' where
      appropriate.
      <GoASpacer vSpacing={"l"} />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          name="intersectionAndCrossingsGroup"
          onChange={(_, value) => {
            setSelection(value);
            setFormError("");
            dispatch(removeAllValidationErrorNotifications());
          }}
          value={selection.toString()}
        >
          {stateConfiguration.configuration.collisionLocation.map(
            (intersectionsAndCrossing) => {
              return (
                <GoARadioItem
                  key={intersectionsAndCrossing.id}
                  label={intersectionsAndCrossing.formLabel}
                  value={intersectionsAndCrossing.id.toString()}
                />
              );
            }
          )}
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={validate}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
